import React from "react";
import { Button } from "../ui/button";
import { EyeIcon } from "lucide-react";
import moment from "moment";
import { cn } from "../../lib/utils";
import PaginatedTable, { PaginatedTableColumn, SortConfig } from "../ui/paginated-table";

interface Invoice {
  id: string;
  status: "Paid" | "Pending" | "Failed";
  amount: number;
  created: string;
}

interface BillingHistoryTableProps {
  invoices: Invoice[];
  loading: boolean;
  onViewInvoice: (invoice: Invoice) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  totalItems?: number;
  sortConfig?: SortConfig<Invoice>;
  onSort?: (key: keyof Invoice) => void;
  showPagination?: boolean;
}

const BillingHistoryTable: React.FC<BillingHistoryTableProps> = ({
  invoices,
  loading,
  onViewInvoice,
  emptyTitle = "No billing history",
  emptyDescription = "No invoices have been generated yet.",
  currentPage,
  onPageChange,
  pageSize,
  totalItems,
  sortConfig,
  onSort,
  showPagination = true
}) => {
  const formatDate = (dateString: string) => {
    const date = moment(dateString);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (dateString: string) => {
    const date = moment(dateString);
    return date.format("h:mm A");
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      "Paid": "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400",
      "Pending": "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400",
      "Failed": "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400",
    };
    return colors[status] || "bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400";
  };

  const columns: PaginatedTableColumn<Invoice>[] = [
    {
      key: "id",
      header: "Invoice",
      width: "w-[25%]",
      sortable: true,
      render: (invoice) => (
        <div className="text-sm font-gc-reg text-foreground">
          {invoice.id}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      width: "w-[15%]",
      sortable: true,
      render: (invoice) => (
        <span className={cn(
          "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
          getStatusColor(invoice.status)
        )}>
          {invoice.status}
        </span>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      width: "w-[20%]",
      sortable: true,
      render: (invoice) => (
        <div className="text-sm font-gc-reg text-foreground">
          ${invoice.amount.toFixed(2)}
        </div>
      ),
    },
    {
      key: "created",
      header: "Created",
      width: "w-[25%]",
      sortable: true,
      render: (invoice) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(invoice.created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(invoice.created)}
          </div>
        </div>
      ),
    },
    {
      key: "id", // Using id as key but this column is for actions
      header: "Actions",
      width: "w-[15%]",
      render: (invoice) => (
        <Button
          variant="ghost"
          size="sm"
          className="gap-1"
          onClick={(e) => {
            e.stopPropagation();
            onViewInvoice(invoice);
          }}
        >
          <EyeIcon className="w-3 h-3" />
          View
        </Button>
      ),
    },
  ];

  return (
    <PaginatedTable<Invoice>
      data={invoices}
      columns={columns}
      loading={loading}
      sortConfig={sortConfig}
      onSort={onSort}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={pageSize}
      totalItems={totalItems}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      onRowClick={(invoice) => onViewInvoice(invoice)}
      showPagination={showPagination}
      loadingRows={5}
    />
  );
};

export default BillingHistoryTable; 